/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import { useCallback } from 'preact/hooks';
import { useTooltip } from '../hooks/UNSAFE_useTooltip';
import { useTranslationBundle } from '../hooks/UNSAFE_useTranslationBundle';
import type { BundleType } from '../resources/nls/bundle';
import { BaseSelector } from '../PRIVATE_BaseSelector';
import type { BaseSelectorProps as Props } from '../UNSAFE_Selector';
import { SelectionDetail } from '../UNSAFE_Collection';

/**
 * Props for the SelectorAll Component
 */
export type SelectorAllProps<K> = Pick<Props, 'aria-label' | 'aria-labelledby' | 'testId'> & {
  /**
   * Callback function to invoke when the selected keys has changed when
   * the checkbox is toggled.
   */
  onChange?: (detail: SelectionDetail<K>) => void;
  /**
   * Whether tooltip shows in SelectorAll or not
   */
  showTooltip?: 'enabled' | 'disabled';
  /**
   * The selected state of the select all selector
   */
  selected: 'none' | 'all' | 'partial' | 'partial-all';
};

/**
 * The SelectorAll component renders checkboxes for select all case.
 */
export function SelectorAll<K>({
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledby,
  showTooltip = 'disabled',
  testId,
  onChange,
  selected
}: SelectorAllProps<K>) {
  const selectionHandler = useCallback(
    (event: Event) => {
      const { key } = event as KeyboardEvent;
      if (key !== undefined && key !== ' ') {
        return;
      }

      if (selected == 'none' || selected == 'partial') {
        onChange?.({ value: { all: true, deletedKeys: new Set() }, target: event.target });
      } else {
        onChange?.({ value: { all: false, keys: new Set() }, target: event.target });
      }

      //selectionHandler would be triggered by the onClick
      event.stopPropagation();
    },
    [onChange, selected]
  );

  const isChecked =
    selected === 'partial' || selected === 'partial-all'
      ? 'partial'
      : selected === 'all'
      ? 'checked'
      : 'unchecked';
  const translations = useTranslationBundle<BundleType>('@oracle/oraclejet-preact');

  const tooltipLabel =
    selected === 'none' || selected === 'partial'
      ? translations.selectorAll_selectAll()
      : translations.selectorAll_deselectAll();

  const { tooltipContent, tooltipProps } = useTooltip({
    text: tooltipLabel,
    isDisabled: showTooltip === 'disabled' ? true : false
  });

  const ariaLabelText = !ariaLabel && !ariaLabelledby ? tooltipLabel : undefined;

  return (
    <BaseSelector
      aria-label={ariaLabel ? ariaLabel : ariaLabelText}
      aria-labelledby={ariaLabelledby}
      onSelectionHandler={selectionHandler}
      isChecked={isChecked}
      tooltipContent={tooltipContent}
      tooltipProps={tooltipProps}
      testId={testId}
    />
  );
}
